import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { GraduationCap, Users, Shield } from "lucide-react";
import { toast } from "react-hot-toast";

export default function RoleSelect({ user, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    initializeUser();
  }, []);

  const initializeUser = async () => {
    // Teachers (admins) always see the role selection screen
    if (user?.role === 'teacher') {
      setInitializing(false);
      return;
    }
    
    // If user has no role set, they're a new user - automatically set as student
    if (!user?.role) {
      try {
        await axios.post(`${API}/auth/set-role`, { role: 'student' });
        setUser({ ...user, role: 'student' });
        navigate("/student");
      } catch (error) {
        console.error("Error setting initial role:", error);
        // Continue to show role select as fallback
        setInitializing(false);
      }
    } else if (user.role === 'student') {
      // Students go directly to their dashboard - they don't get to choose
      navigate("/student");
    } else {
      setInitializing(false);
    }
  };

  const selectViewMode = (viewAs) => {
    setLoading(true);
    // For teachers, we don't change their actual role in the database
    // We just navigate them to the appropriate dashboard
    if (viewAs === "teacher") {
      navigate("/teacher");
    } else {
      navigate("/student");
    }
    setLoading(false);
  };

  // Show loading while checking/setting initial role
  if (initializing) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Setting up your account...</p>
        </div>
      </div>
    );
  }

  // Only teachers see this selection screen
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Shield className="w-6 h-6 text-amber-600" />
            <span className="text-sm font-medium text-amber-600 uppercase tracking-wide">Admin Access</span>
          </div>
          <h1 className="font-serif text-3xl font-bold text-slate-900 mb-3">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600">
            As an admin, choose which view you'd like to enter
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Teacher/Admin Card */}
          <Card 
            data-testid="role-teacher-card"
            className="cursor-pointer border-2 border-amber-200 bg-amber-50/30 hover:border-amber-400 transition-all duration-300 card-hover"
            onClick={() => !loading && selectViewMode("teacher")}
          >
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-amber-700" />
              </div>
              <CardTitle className="font-serif text-xl">Teacher Dashboard</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <CardDescription className="text-sm leading-relaxed">
                Access the admin panel to create assignments, review student work, 
                manage users, provide feedback, and oversee all discipleship activities.
              </CardDescription>
              <Button 
                data-testid="select-teacher-btn"
                className="mt-6 w-full bg-amber-600 hover:bg-amber-700 rounded-full"
                disabled={loading}
              >
                Enter as Teacher
              </Button>
            </CardContent>
          </Card>

          {/* Student View Card */}
          <Card 
            data-testid="role-student-card"
            className="cursor-pointer border-2 border-transparent hover:border-slate-300 transition-all duration-300 card-hover"
            onClick={() => !loading && selectViewMode("student")}
          >
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-slate-700" />
              </div>
              <CardTitle className="font-serif text-xl">Student View</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <CardDescription className="text-sm leading-relaxed">
                View the platform as a student would see it. Useful for testing 
                the student experience or completing your own assignments.
              </CardDescription>
              <Button 
                data-testid="select-student-btn"
                className="mt-6 w-full bg-slate-800 hover:bg-slate-900 rounded-full"
                disabled={loading}
              >
                Enter as Student
              </Button>
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-sm text-slate-500 mt-8">
          You can switch between views anytime from your profile menu.
        </p>
      </div>
    </div>
  );
}
